import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface GraceSchedule {
  id: string;
  service_key: string;
  disable_mode: string;
  phase: string;
  grace_starts_at: string;
  grace_ends_at: string;
  reason: string | null;
}

/**
 * Returns active grace-period schedules for the current org.
 * Used to show expiring service banners and block recruiter actions.
 */
export function useServiceGraceStatus(serviceKey?: string) {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  const query = useQuery({
    queryKey: ['service-grace-status', orgId, serviceKey],
    queryFn: async () => {
      let q = supabase
        .from('service_disable_schedules')
        .select('id, service_key, disable_mode, phase, grace_starts_at, grace_ends_at, reason')
        .eq('scope_type', 'org')
        .eq('scope_id', orgId!)
        .eq('phase', 'grace_active');

      if (serviceKey) {
        q = q.eq('service_key', serviceKey);
      }

      const { data, error } = await q.order('grace_ends_at', { ascending: true });

      if (error) {
        console.warn('[useServiceGraceStatus]', error.message);
        return [];
      }
      return (data || []) as GraceSchedule[];
    },
    enabled: !!orgId,
    staleTime: 60 * 1000,
  });

  const isInGrace = (query.data?.length ?? 0) > 0;
  const nearestExpiry = query.data?.[0]?.grace_ends_at || null;

  return {
    ...query,
    isInGrace,
    nearestExpiry,
    graceSchedules: query.data || [],
  };
}
